import React, { memo, useState } from 'react';
import Link from 'next/link';

import get from 'lodash/get';
import { INTERNAL_URI, productHref } from '@helpers/route';

import { SearchProductQuery } from 'src/generated/graphql';
import {
  ProductContainer,
  ProductRow,
  ProductImage,
  ProductName,
  ProductQuantity,
  ProductButton,
} from './styled';
import { LazyLoadImage } from '@views/components/lazy-image';
import { Button, FormControl } from 'react-bootstrap';
import { DELAY } from '@helpers/const';
import _scrollToTop from '@helpers/scroll-top';

import { useAlert } from 'react-alert';
import useLayoutContext from '@views/layouts/hooks/use-context';
import useBreakpoint, { Breakpoints } from '@hooks/use-break-point';
import useTimeout from '@restart/hooks/useTimeout';
import {
  EVENT_CLASS_NAME,
  EVENT_ACTION_NAME,
  EVENT_CATEGORY,
  EVENT_LABEL,
} from '@helpers/events';

export type IProps = {
  // data: SearchProductQuery;
  products: any;
};

import CardPrice from '@views/components/product/card/CardPrice';
import { formatCurrencyVN as formatCurrency } from '@helpers/format-currency';
import _percentage from '@helpers/percentage';

type ProductPriceProps = {
  quantityAvailable: number;
  isContact: boolean;
  price: number;
  listPrice: number;
  sale: boolean;
};

const ProductPrice = React.memo<ProductPriceProps>(
  ({ quantityAvailable, isContact, price, listPrice, sale }) => {
    const contentPrice = (() => {
      if (quantityAvailable <= 0) {
        return <CardPrice.NewPrice>Hết hàng</CardPrice.NewPrice>;
      }

      if (isContact) {
        return <CardPrice.NewPrice>Liên hệ</CardPrice.NewPrice>;
      }

      return (
        <>
          <CardPrice.NewPrice>{formatCurrency(price)}</CardPrice.NewPrice>
          {/* {sale && (
            <>
              <CardPrice.OldPrice>
                {formatCurrency(listPrice)}
              </CardPrice.OldPrice>
              <CardPrice.BadgeSale>
                <span>-{_percentage(price, listPrice)}%</span>
              </CardPrice.BadgeSale>
            </>
          )} */}
        </>
      );
    })();

    return <CardPrice>{contentPrice}</CardPrice>;
  }
);

const ProductItem = ({
  id,
  price,
  listPrice,
  imageUrl,
  name,
  isContactProduct,
  sale,
  quantityAvailable,
  onAddProduct,
}) => {
  const [quantity, setQuantity] = useState(1);
  const url = productHref(id, name);
  const handleAddToCard = () => {
    onAddProduct({ id, quantity, name, price });
  };

  return (
    <ProductRow key={id}>
      <ProductImage>
        <Link href={INTERNAL_URI.PRODUCT} as={url}>
          <a
            className={EVENT_CLASS_NAME}
            data-category={EVENT_CATEGORY.PRODUCT_QUOTATION}
            data-action={EVENT_ACTION_NAME}
            data-label={name}
          >
            <LazyLoadImage
              image={{
                src: imageUrl,
                alt: name,
                placeholderSrc: '/assets/default.png',
                width: '100%',
              }}
            />
          </a>
        </Link>
      </ProductImage>
      <ProductName>
        <Link href={INTERNAL_URI.PRODUCT} as={url}>
          <a
            className={EVENT_CLASS_NAME}
            data-category={EVENT_CATEGORY.PRODUCT_QUOTATION}
            data-action={EVENT_ACTION_NAME}
            data-label={name}
          >
            {name}
          </a>
        </Link>
        <ProductPrice
          isContact={isContactProduct}
          price={price}
          listPrice={listPrice}
          sale={!!sale}
          quantityAvailable={quantityAvailable}
        />
      </ProductName>
      <ProductQuantity>
        <FormControl
          type="number"
          value={quantity}
          onChange={(event) => setQuantity(parseInt(event.target.value, 10))}
          min={1}
        />
      </ProductQuantity>
      <ProductButton>
        <Button
          type="button"
          onClick={handleAddToCard}
          disabled={quantityAvailable === 0}
          className={EVENT_CLASS_NAME}
          data-category={EVENT_CATEGORY.PRODUCT_QUOTATION_ADD_CART}
          data-action={EVENT_ACTION_NAME}
          data-label={name}
        >
          Thêm vào giỏ hàng
        </Button>
      </ProductButton>
    </ProductRow>
  );
};

const Products = (props: IProps) => {
  const { products } = props;
  const alert = useAlert();
  const isMobile = [Breakpoints.xs, Breakpoints.sm].includes(useBreakpoint());

  const {
    authenticated,
    checkout: { handleAddItem },
    setActiveCartToast,
  } = useLayoutContext();
  const cartToastTimer = useTimeout();

  const onAddProduct = async ({ id, quantity = 1, name, price }) => {
    dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    dataLayer.push({
      event: 'addToCart',
      ecommerce: {
        currencyCode: 'VND',
        add: {
          // 'add' actionFieldObject measures.
          products: [
            {
              //  adding a product to a shopping cart.
              name: name,
              id: id,
              price: price,
              brand: '',
              category: 'QUOTATION',
              variant: '',
              quantity,
            },
          ],
        },
      },
    });

    try {
      await handleAddItem({ id: parseInt(id, 10), quantity });
      _scrollToTop();
      if (isMobile) {
        alert.info('Thêm vào giỏ hàng thành công');
      } else {
        setActiveCartToast(true);
        cartToastTimer.set(() => {
          setActiveCartToast(false);
        }, DELAY);
      }
    } catch (error) {
      alert.error(
        get(
          error,
          'message',
          get(
            error,
            'graphQLErrors[0].message',
            'Có lỗi xảy ra vui lòng thử lại'
          )
        )
      );
    }
  };

  return (
    <>
      {products.map((product: any) => {
        const price = get(product, 'price', 0);
        const listPrice = get(product, 'attributes.listPrice');
        const sale: number = !!listPrice ? listPrice - price : 0;

        return (
          <ProductItem
            key={product.productId}
            id={product.productId}
            price={price}
            listPrice={listPrice}
            sale={sale}
            imageUrl={product.imageUrl}
            isContactProduct={product.isContactProduct}
            name={product.name}
            quantityAvailable={product.quantity}
            onAddProduct={onAddProduct}
          />
        );
      })}
    </>
  );
};

const Wrapper = memo<IProps>(Products);
Wrapper.displayName = 'Products';

export default Wrapper;
